import { useInfiniteQuery } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";

/** A stage in the renewal cadence — the same set the Günüm renewalPipeline aggregate
 *  buckets by (mirrors advisory.renewal_schedule.current_stage). Amber ONLY at T-7,
 *  red ONLY for the expired bucket (EXPIRY/POST_EXPIRY) — the funnel's tone contract. */
export type RenewalStage =
  | "T_MINUS_45"
  | "T_MINUS_30"
  | "T_MINUS_15"
  | "T_MINUS_7"
  | "EXPIRY"
  | "POST_EXPIRY";

/** One upcoming renewal — a GET /v1/renewals row (#222): masked, own-scope. The exact
 *  backend shape (queue-read.service `RenewalListRow`). `householdName` is the
 *  non-sensitive household label (no member name/phone/TCKN); `dueDate` is the term end
 *  (ISO over the wire); `expired` is the genuine lapse flag (EXPIRY/POST_EXPIRY);
 *  `lapsedUninsured24h` is the PRECISE duty-of-care flag (term lapsed ≤24h AND the
 *  asset has no other in-term ACTIVE policy). NO commission, NO forecast. */
export interface RenewalListRow {
  id: string;
  householdId: string;
  householdName: string;
  stage: RenewalStage;
  dueDate: string;
  status: "ACTIVE" | "COMPLETED" | "LAPSED";
  expired: boolean;
  lapsedUninsured24h: boolean;
}

/** The standard list envelope (frozen API contract): opaque cursor, no offsets. */
interface RenewalPage {
  data: RenewalListRow[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** GET /v1/renewals — the advisor's active renewals list (#222), own-scope + masked,
 *  urgency-ordered by the SERVER (soonest / expired first — never re-sorted here). The
 *  per-renewal drill-down beneath the cadence funnel; its total reconciles with the
 *  funnel's renewalPipeline.total for the same book. Cursor-paginated via "Daha fazla".
 *  No retry — an empty book is a state, not a transient. Mirrors the usePartyList /
 *  useBookSummary Page-read pattern. */
export function useRenewalsList(pageSize = 20) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ["renewals-list", pageSize],
    initialPageParam: null as string | null,
    retry: false,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: String(pageSize) });
      if (pageParam) p.set("cursor", pageParam);
      return api.get<RenewalPage>(`/v1/renewals?${p.toString()}`);
    },
    getNextPageParam: (last) => (last?.hasMore ? last.nextCursor : undefined),
  });
}
